import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

const AlertItem = ({ n }) => (
  <li data-testid={`notification-${n.id}`} className={`px-4 py-3 text-sm ${n.read_at ? "" : "bg-gold-light/40"}`}>
    <p className="font-medium text-navy">{n.kind === "overdue" ? "Overdue: " : "Coming up: "}{n.title}</p>
    <p className="text-xs text-slate-500">Due {formatDate(n.due_date)} · {n.workspace_name}</p>
  </li>
);

const DueList = ({ items, wid, tone, testId, empty }) => (
  <ul data-testid={testId} className="divide-y">
    {items.length ? items.map((d) => (
      <li key={d.id} className="px-4 py-2.5 text-sm">
        <Link to={d.source === "ip" ? `/app/w/${wid}/ip/${d.asset_id}` : `/app/w/${wid}/deadlines`} className="font-medium text-navy hover:underline">{d.title}</Link>
        <p className={`text-xs ${tone}`}>Due {formatDate(d.due_date)}</p>
      </li>
    )) : <li className="px-4 py-3 text-xs text-slate-500">{empty}</li>}
  </ul>
);

export const NotificationBell = ({ workspace, light = false }) => {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [due, setDue] = useState(null);
  const load = useCallback(() => { api.get("/notifications").then(({ data: d }) => setData(d)).catch(() => {}); }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const onOpen = (open) => {
    if (!open) return;
    api.get(`/workspaces/${workspace.id}/alerts`).then(({ data: d }) => setDue(d)).catch(() => setDue({ overdue: [], upcoming: [] }));
    if (data.unread) api.post("/notifications/read", {}).then(() => setTimeout(load, 1500)).catch(() => {});
  };

  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger data-testid="notification-bell-button" aria-label="Alerts" className={`relative rounded-md p-2 ${light ? "text-white hover:bg-white/10" : "text-navy hover:bg-navy/5"}`}>
        <Bell className="h-5 w-5" />
        {data.unread > 0 && <span data-testid="notification-unread-count" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-gold px-1 text-[10px] font-bold text-navy">{data.unread}</span>}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] max-w-[calc(100vw-2rem)] p-0" data-testid="notification-panel">
        <Tabs defaultValue="due">
          <TabsList className="m-2 grid grid-cols-2"><TabsTrigger value="due" data-testid="notification-tab-due">Due dates</TabsTrigger><TabsTrigger value="alerts" data-testid="notification-tab-alerts">Alerts</TabsTrigger></TabsList>
          <TabsContent value="due" className="mt-0 max-h-[60vh] overflow-y-auto">
            <p className="px-4 pt-1 text-[11px] font-semibold uppercase tracking-wider text-red-700">Overdue</p>
            <DueList items={due?.overdue || []} wid={workspace.id} tone="text-red-700" testId="notification-overdue-list" empty="Nothing overdue." />
            <p className="px-4 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Next 14 days</p>
            <DueList items={due?.upcoming || []} wid={workspace.id} tone="text-slate-500" testId="notification-upcoming-list" empty="No confirmed dates in the next 14 days." />
          </TabsContent>
          <TabsContent value="alerts" className="mt-0 max-h-[60vh] overflow-y-auto">
            <ul className="divide-y" data-testid="notification-alerts-list">
              {data.notifications.length ? data.notifications.map((n) => <AlertItem key={n.id} n={n} />)
                : <li className="px-4 py-3 text-xs text-slate-500">No alerts yet. Reminders for confirmed dates will appear here.</li>}
            </ul>
          </TabsContent>
        </Tabs>
        <p className="border-t px-4 py-2 text-[11px] text-slate-500">Only confirmed dates. <Link to={`/app/w/${workspace.id}/settings#notifications`} className="underline" data-testid="notification-settings-link">Email settings</Link></p>
      </PopoverContent>
    </Popover>
  );
};
